import { Constructable, PublicInterfaceOf } from 'derivita/ts/types/types';
import { Singleton } from 'derivita/ts/types/singleton';

export class ServiceResolutionError extends Error {}

export function defineService<T>(
  name: string,
  type?: Constructable<T>
): ServiceDefinition<T> {
  return {
    name,
  };
}

export interface ServiceDefinition<T> {
  name: string;
}

export type Service<T> = ServiceDefinition<T> | Constructable<T> | Singleton<T>;

export interface ServiceContainer {
  register<T>(type: Service<T>, instance: PublicInterfaceOf<T>): void;
  resolve<T>(type: Service<T>): T;
  exists(type: Service<unknown>): boolean;
}

export class Container implements ServiceContainer {
  private instances = new Map<Service<unknown>, unknown>();

  register<T>(type: Service<T>, instance: PublicInterfaceOf<T>) {
    if (this.instances.has(type)) {
      throw new ServiceResolutionError(
        `${type.name} has already been registered`
      );
    }

    this.instances.set(type, instance);
  }

  resolve<T>(type: Service<T>): T {
    const instance = this.instances.get(type);
    if (instance === undefined) {
      throw new ServiceResolutionError(`${type.name} has not been registered.`);
    }
    return instance as T;
  }

  exists(type: Service<unknown>): boolean {
    return this.instances.has(type);
  }
}

let container: ServiceContainer;

export function registerGlobalContainer(
  globalContainer: ServiceContainer
): void {
  if (container) {
    throw new ServiceResolutionError('Service container already registered');
  }
  container = globalContainer;
}

export function register<T>(
  type: Service<T>,
  instance: PublicInterfaceOf<T>
): void {
  if (!container) {
    container = new Container();
  }
  container.register(type, instance);
}

export function resolve<T>(type: Service<T>): T {
  if (!container) {
    throw new ServiceResolutionError(
      'Service container not registered. Did you forget to call registerGlobalContainer or registerTestServiceContainer?'
    );
  }
  return container.resolve(type);
}

export function serviceExists(type: Service<unknown>): boolean {
  if (!container) {
    return false;
  }
  return container.exists(type);
}
